/**
 * Client-side image compression — used before uploading receipt photos
 * so we don't push 3-5MB JPEGs over mobile data when 200-400KB is
 * indistinguishable for receipt readability.
 *
 * Pipeline:
 *   1. Decode the file into an HTMLImageElement (so we get full
 *      EXIF-orientation handling for free in modern browsers).
 *   2. Compute a scale factor that brings the longest edge to
 *      `maxDim` without upscaling smaller images.
 *   3. Draw onto a canvas and re-encode as JPEG at `quality`.
 *
 * Returns a Blob (or the original File on failure — receipt
 * record-keeping is more important than the bandwidth saving).
 *
 *   compressImage(file)
 *   compressImage(file, { maxDim: 1200, quality: 0.7 })
 */
export async function compressImage(file, { maxDim = 1600, quality = 0.75 } = {}) {
  if (!file || !file.type?.startsWith('image/')) return file;

  // Skip work if the file is already comfortably small. JPEG headers
  // mean we can't be 100% sure of dimensions, but ~300KB is a sane
  // skip threshold — re-encoding a tiny image just costs CPU.
  if (file.size && file.size < 300 * 1024) return file;

  try {
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = dataUrl;
    });

    const longest = Math.max(img.naturalWidth, img.naturalHeight);
    const scale = longest > maxDim ? maxDim / longest : 1;
    const w = Math.round(img.naturalWidth * scale);
    const h = Math.round(img.naturalHeight * scale);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);

    const blob = await new Promise((resolve) => {
      canvas.toBlob(
        (b) => resolve(b),
        'image/jpeg',
        quality,
      );
    });
    if (!blob) return file;

    // If compression somehow made the file bigger (rare but possible
    // on already-tight JPEGs), keep the original.
    return blob.size < file.size ? blob : file;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Image compression failed; uploading original.', err);
    return file;
  }
}
